import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Text, useMainContext, useWindowSize } from "../../../modules/Dashboard";
import CardLayout from "../../../components/layouts/CardLayout";
import { MOBILE_SIZE } from "../../../core/constants/common";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";
import { LayoutGroup, motion } from "framer-motion";

const Tabs = ({
    tabs,
    useRouting = false,
    tabClassName = "",
    activeTabClassName = "",
}) => {
    const { width } = useWindowSize();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        main: { lang },
    } = useMainContext();

    const DIR = lang === "en" ? "ltr" : "rtl";
    const [activeTab, setActiveTab] = useState(0);

    const queryParams = new URLSearchParams(location.search);
    const tabFromUrl = queryParams.get("tab");

    const findTabIndexByKey = (key) =>
        tabs.findIndex((t) => t.key?.toLowerCase() === key?.toLowerCase());

    useEffect(() => {
        if (useRouting) {
            const urlIndex = findTabIndexByKey(tabFromUrl);
            if (urlIndex !== -1 && urlIndex !== activeTab) {
                setActiveTab(urlIndex);
            }
        }
    }, [useRouting, tabFromUrl]);

    const handleTabChange = (index, disabled) => {
        if (disabled) return;

        setActiveTab(index);

        if (useRouting) {
            const newParams = new URLSearchParams(location.search);
            newParams.set("tab", tabs[index].key);
            navigate({ search: newParams.toString() }, { replace: true });
        }
    };

    return (
        <div className="w-full p-4">
            <CardLayout className="p-1 !bg-[#15181D] !rounded-xl">
                <LayoutGroup id="tabs">
                    <div
                        className="flex gap-2 w-full overflow-x-auto items-center scrollbar-hide relative"
                        style={{ direction: DIR }}
                    >
                        {tabs.map((tab, index) => {
                            const { icon: Icon } = tab;
                            const isActive = activeTab === index;
                            const isDisabled = tab.disabled;

                            const buttonClasses = twMerge(
                                clsx(
                                    tabClassName,
                                    "relative transition-all text-sm font-medium flex flex-col items-center justify-center gap-1 rounded-lg px-3 sm:px-6 py-2",
                                    isDisabled && "opacity-50 cursor-not-allowed"
                                )
                            );

                            const textClasses = twMerge(
                                clsx(
                                    "transition-all whitespace-nowrap lg:text-lg text-white",
                                    isActive && !activeTabClassName
                                        ? "font-medium"
                                        : "font-normal"
                                )
                            );

                            return (
                                <motion.button
                                    key={tab.key || index}
                                    layout
                                    className={buttonClasses}
                                    onClick={() => handleTabChange(index, isDisabled)}
                                >
                                    {/* ACTIVE INDICATOR */}
                                    {isActive && <TabIndicator />}

                                    <div className="relative z-10 flex flex-col items-center gap-1">
                                        {Icon && (
                                            <Icon
                                                size={width <= MOBILE_SIZE ? 20 : 28}
                                                color={isActive ? "#FC6432" : "#FFFFFF"}
                                            />
                                        )}

                                        <Text tid={tab.label} className={textClasses} />

                                        {tab.description && (
                                            <span className="text-[10px] text-gray-400 mt-1">
                                                <Text tid={tab.description} />
                                            </span>
                                        )}
                                    </div>
                                </motion.button>
                            );
                        })}
                    </div>
                </LayoutGroup>
            </CardLayout>

            <div className="mt-4" style={{ direction: DIR }}>
                {tabs[activeTab]?.content}
            </div>
        </div>
    );
};

export default Tabs;


const TabIndicator = () => (
    <motion.div
        layoutId="tab-indicator"
        className="tab-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        exit={{ opacity: 0 }}
        transition={{
            type: "spring",
            stiffness: 520,
            damping: 36,
        }}
    />
);
